import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useBuildingAggregates } from "./hooks/useBuildingAggregates";
import { buildAttentionItems, issueTone } from "./digestUtils";
import type { Building } from "./digestUtils";
import Card from "../../components/ui/Card";
import StatusDot from "../../components/ui/StatusDot";
import TrendBadge from "../../components/ui/TrendBadge";
import PriorityBadge from "../../components/ui/PriorityBadge";
import Spinner from "../../components/ui/Spinner";

type Props = {
  buildings: Building[];
  startDate: string | null;
  endDate: string | null;
};

const MAX_ROWS = 8;

// Left-to-right chip order within a row. Wellness leads: a resident nobody has
// seen is the most time-sensitive thing here, and unlike an alarm it gets worse
// while nobody is looking. Matches SensorAttention so a property and its
// residents read in the same order.
const CHIP_ORDER: Record<string, number> = {
  notSeen: -2,
  declining: 0,
  shutdowns: 1,
  alerts: 2,
  noCooking: 2.5,
  offline: 3,
};

/**
 * Command-center "needs attention" feed shown above the buildings table. Surfaces
 * the most urgent properties first (auto shut-offs → alerts → declining trend →
 * offline sensors) so an operator sees what's wrong without scanning the table.
 *
 * Reuses `useBuildingAggregates`, which React Query dedupes by queryKey, so
 * mounting this alongside the table costs no extra network requests. Counts
 * honor the active date range; trend uses the hook's own 28-day window. This is
 * not a real-time "danger now" signal (that needs per-sensor device polling).
 */
export default function PortfolioOverview({
  buildings,
  startDate,
  endDate,
}: Props) {
  const navigate = useNavigate();
  const { aggregates, wellnessByBuilding } = useBuildingAggregates(
    buildings,
    startDate,
    endDate
  );

  const items = useMemo(
    () => buildAttentionItems(buildings, aggregates, wellnessByBuilding),
    [buildings, aggregates, wellnessByBuilding]
  );

  // Show one spinner until every building's aggregate has fully settled
  // (alerts/shutdowns included), then render the complete list at once with no
  // progressive populate. Cached data makes this instant on return visits, and
  // background refetches no longer count as "loading", so it won't re-spin.
  const ready = useMemo(
    () =>
      buildings.every((b) => {
        const s = aggregates.get(b.buildingId)?.aggStatus;
        return s === "ready" || s === "error";
      }),
    [buildings, aggregates]
  );

  if (!buildings.length) return null;

  const visible = items.slice(0, MAX_ROWS);
  const overflow = items.length - visible.length;

  return (
    <section className="mb-6" aria-label="Properties needing attention">
      <div className="flex items-center flex-wrap gap-3 mb-3">
        <h2 className="text-xl font-semibold text-white">Needs attention</h2>
        {items.length > 0 && (
          <span className="inline-flex items-center justify-center min-w-[1.75rem] h-7 px-2 rounded-chip bg-alert-critical/20 text-alert-critical text-sm font-semibold">
            {items.length}
          </span>
        )}
        <span
          className="text-xs text-ink-3"
          title="Priority ranking uses safety trend over the last 4 weeks, independent of the date picker. Alert and shut-off chips honor the date range."
        >
          Ranked by the last 4 weeks
        </span>
      </div>

      <Card tone="light" padded={false}>
        {!ready ? (
          <div className="p-10 flex items-center justify-center gap-3 text-ink-3">
            <Spinner size={36} />
            <span>Checking properties…</span>
          </div>
        ) : items.length === 0 ? (
          <div className="p-6 flex items-center justify-center gap-2 text-ink-2">
            <StatusDot tone="success" />
            <span className="font-medium">
              No properties need attention in this range.
            </span>
          </div>
        ) : (
          <ul className="divide-y divide-black/5">
            {visible.map((item) => {
              const orderedIssues = [...item.issues].sort(
                (a, b) =>
                  (CHIP_ORDER[a.kind] ?? 99) - (CHIP_ORDER[b.kind] ?? 99)
              );
              return (
                <li key={item.buildingId}>
                  <button
                    type="button"
                    onClick={() =>
                      navigate(`/insights/properties/${item.buildingId}`)
                    }
                    className="w-full flex items-center gap-4 px-5 py-3 text-left hover:bg-black/[0.03] focus:outline-none focus-visible:bg-black/[0.05] transition-colors"
                  >
                    <span className="w-20 shrink-0 flex">
                      <PriorityBadge
                        score={item.priorityScore}
                        band={item.priorityBand}
                      />
                    </span>
                    <span className="font-semibold text-ink truncate flex-1 min-w-0">
                      {item.name}
                    </span>
                    <span className="flex items-center gap-2 flex-wrap justify-end">
                      {orderedIssues.map((issue) =>
                        issue.kind === "declining" ? (
                          <TrendBadge
                            key={issue.kind}
                            level={item.trendLevel ?? undefined}
                            size={16}
                          />
                        ) : (
                          <span
                            key={issue.kind}
                            className="inline-flex items-center gap-1.5 rounded-chip bg-black/5 px-2.5 py-1 text-xs font-semibold text-ink-2"
                          >
                            <StatusDot tone={issueTone(issue.kind)} />
                            {issue.text}
                          </span>
                        )
                      )}
                    </span>
                    <ChevronRightIcon
                      className="text-ink-3 shrink-0"
                      fontSize="small"
                    />
                  </button>
                </li>
              );
            })}
            {overflow > 0 && (
              <li className="px-5 py-2.5 text-sm text-ink-3">
                +{overflow} more {overflow === 1 ? "property" : "properties"} in
                the table below.
              </li>
            )}
          </ul>
        )}
      </Card>
    </section>
  );
}
